/**
 * TableCellInput — canonical faint-box inline editor for data-table cells.
 *
 * Reference implementation: POPrintPage Actual Qty / Received Qty columns.
 * Always visible; commit on blur or Enter; Esc cancels. No click-to-edit,
 * no MUI standard underline, no number spinners.
 */
import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import { BRAND } from '../theme/tokens';

/** Shared MUI sx — faint grey border, brand on hover/focus */
export const TABLE_CELL_INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#e0e0e0' },
    '&:hover fieldset': { borderColor: BRAND },
    '&.Mui-focused fieldset': { borderColor: BRAND },
  },
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  '& input[type=number]': { MozAppearance: 'textfield' },
};

export interface TableCellInputProps {
  value: string;
  onCommit: (value: string) => void;
  align?: 'left' | 'center' | 'right';
  width?: number | string;
  fontSize?: string;
  fontWeight?: number;
  color?: string;
  placeholder?: string;
  /** Override default faint border — e.g. received-qty state colours */
  borderColor?: string;
  disabled?: boolean;
  monospace?: boolean;
  /** Restrict typing to decimal numbers */
  numeric?: boolean;
  'aria-label'?: string;
}

export const TableCellInput: React.FC<TableCellInputProps> = ({
  value,
  onCommit,
  align = 'left',
  width,
  fontSize = '0.75rem',
  fontWeight = 700,
  color = '#555',
  placeholder,
  borderColor,
  disabled,
  monospace,
  numeric,
  'aria-label': ariaLabel,
}) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);

  const commit = () => {
    if (draft !== value) onCommit(draft);
    else setDraft(value);
  };

  return (
    <TextField
      value={draft}
      disabled={disabled}
      size="small"
      placeholder={placeholder}
      onChange={e => {
        const v = e.target.value;
        if (numeric && v !== '' && !/^-?\d*\.?\d*$/.test(v)) return;
        setDraft(v);
      }}
      onBlur={commit}
      onFocus={e => e.target.select()}
      onKeyDown={e => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') { setDraft(value); (e.target as HTMLInputElement).blur(); }
      }}
      inputProps={{
        inputMode: numeric ? 'decimal' : 'text',
        'aria-label': ariaLabel,
        style: {
          textAlign: align,
          padding: '2px 4px',
          width: typeof width === 'number' ? width : undefined,
          fontSize,
          fontWeight,
          color,
          fontFamily: monospace ? 'monospace' : 'inherit',
        },
      }}
      sx={{
        width: width ?? '100%',
        ...TABLE_CELL_INPUT_SX,
        ...(borderColor && {
          '& .MuiOutlinedInput-root fieldset': { borderColor },
        }),
      }}
    />
  );
};
